import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import styled from "styled-components";
import CreateFuzzySearchFilter from "./fuzzySearch";
import { Autocomplete, Chip, TextField } from "@mui/material";
import type { AutocompleteInputChangeReason } from "@mui/material/Autocomplete";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";

export type GetOptionLabel<T> = (option: T) => string;
export type GetOptionKey<T> = (option: T) => string;

// Props shared by both single- and multi-select modes
interface SearchableDropdownCommonProps<T> {
  //HTML props
  className?: string; //Optional CSS class on the outer wrapper
  id?: string; //component id on the outer wrapper
  name?: string; //Name applied to the underlying MUI TextField (useful for forms)
  tabIndex?: number; //controls tab order

  //Core functionality
  options?: T[]; // Options to show in the dropdown

  // UX settings
  clearable?: boolean; //Indicates if the selected options are clearable
  // Debounce time in milliseconds for multi-select change notifications.
  // When > 0, calls to the handleValueChange (multi) are delayed until the
  // user pauses for this time. Default is 500.
  debounceMs?: number;
  disabled?: boolean; //disables the control
  placeholder?: string; // Placeholder text to show
  searchable?: boolean; //Indicates if the options are searchable

  // Sizing and styling
  backgroundColor?: string; //background color of the TextField
  height?: string; //Height of the TextField
  noHoverBorder?: boolean; //show/hide hover border on outer wrapper
  noShadow?: boolean; //show/hide shadow on outer wrapper
  style?: React.CSSProperties; //pass css properties to outer wrapper

  // Event handlers
  onBlur?: React.FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>; //onBlur for TextField input

  // Functions to determine the display label and key for each option
  // These will default to name and value attributes of the options object
  // if delegates are not provided.
  getOptionLabel?: GetOptionLabel<T>;
  getOptionKey?: GetOptionKey<T>;

  /**
   * Optional Chip props for selected-value chips (single-select adornment and
   * multi-select tags). Use to match LinkChip / Table Summary coloring.
   */
  getChipProps?: (option: T) => Partial<React.ComponentProps<typeof Chip>>;
}

// Single-select variant (default when `multiselect` is omitted or false)
// Value is single object
export interface SingleSelectProps<T> extends SearchableDropdownCommonProps<T> {
  multiselect?: false; //single selection dropdown
  value: T | null; // Current value (null when empty)

  // Delegate to call when the selected option changes.
  // Notified with the underlying key (or null) and the selected object (or null)
  handleValueChange?: (valueKey: string | null, valueObject: T | null) => void;
}

// Multi-select variant (activated when `multiselect` is true)
// Value is an array of objects
export interface MultiSelectProps<T> extends SearchableDropdownCommonProps<T> {
  multiselect: true; //multi-selection dropdown
  value: T[]; //Current values (empty array when none)

  // Delegate to call when the selected options change.
  // Notified with the underlying keys and the selected objects
  handleValueChange?: (valueKeys: string[], valueObjects: T[]) => void;
}

// Full SearchableDropdownProps class
// Union of the SingleSelectProps and MultiSelectProps
export type SearchableDropdownProps<T> =
  | SingleSelectProps<T>
  | MultiSelectProps<T>;

/** ---------- Helper methods ---------- */
const defaultGetOptionLabel = (o: any): string => {
  // null / undefined -> empty string
  if (o == null) return "";
  if (o == undefined) return "";

  // boolean -> display as TRUE and FALSE
  if (typeof o === "boolean") return o === true ? "TRUE" : "FALSE";

  // string or number, just convert to string
  if (typeof o === "string" || typeof o === "number") return String(o);

  // prefer explicit label-like properties
  if (o.label) return String(o.label);
  if (o.Label) return String(o.Label);
  if (o.name) return String(o.name);
  if (o.Name) return String(o.Name);

  // nothing useful found — return empty string as fallback
  return "";
};

// externalized default getOptionKey function so it is fully stabilized
// Default function will return self for primitive string or number.
// Then it will look for key, Key, id, Id, value, Value, key
// Returns "" if nothing else works
// Caller should supply this handler for any complex type.
const defaultGetOptionKey = (o: any): string => {
  // null / undefined -> empty string
  if (o == null) return "";

  // primitive -> just convert to string
  if (typeof o === "string" || typeof o === "number" || typeof o === "boolean")
    return String(o);

  // prefer explicit key-like properties (accept 0 and "" as valid values)
  if ((o.key ?? false) || o.key === 0 || o.key === "") return String(o.key);
  if ((o.Key ?? false) || o.Key === 0 || o.Key === "") return String(o.Key);
  if ((o.id ?? false) || o.id === 0 || o.id === "") return String(o.id);
  if ((o.Id ?? false) || o.Id === 0 || o.Id === "") return String(o.Id);
  if ((o.value ?? false) || o.value === 0 || o.value === "")
    return String(o.value);
  if ((o.Value ?? false) || o.Value === 0 || o.Value === "")
    return String(o.Value);

  // nothing useful found — return empty string as fallback
  return "";
};

// helper to determine if two string arrays have the same values
// where order does not matter.
const isSameKeySet = (a: string[] | null, b: string[] | null) => {
  if (a === b) return true;
  if (!a || !b) return false;
  if (a.length !== b.length) return false;
  const setA = new Set(a);
  return b.every((k) => setA.has(k));
};

/* ---------- Component overloads for better inference ---------- */
// If multiselect is true, require the MultiSelectProps shape
export function SearchableDropdown<T>(props: MultiSelectProps<T>): JSX.Element;
// If multiselect is false or omitted, use SingleSelectProps
export function SearchableDropdown<T>(props: SingleSelectProps<T>): JSX.Element;

/* ---------- Component Implementation signature ---------------- */
export function SearchableDropdown<T>({
  // HTML props
  className = "SearchableDropdown",
  id,
  name = "SearchableDropdown",
  tabIndex,

  // Core functionality
  multiselect, // sub-type discriminator (do NOT default here)
  options = [],
  value,

  // UX settings
  clearable = false,
  debounceMs = 500,
  disabled = false,
  placeholder,
  searchable = false,

  // Sizing and styling
  backgroundColor = "white",
  height,
  noShadow = false,
  noHoverBorder = false,
  style,

  // Event handlers
  getOptionLabel = defaultGetOptionLabel, // Function for getting an option's label
  getOptionKey = defaultGetOptionKey, // Function for getting an option's Key
  getChipProps,
  handleValueChange, //callback to handle a change on an input
  onBlur,
}: SearchableDropdownProps<T>): JSX.Element {
  // State variables
  const [inputDisplay, setInputDisplay] = useState<string>(""); //controls input text
  const [dropdownOpen, setDropdownOpen] = useState<boolean>(false); //controls whether dropdown is open
  // Only for multiselect mode we keep an internal array to show optimistic changes
  const [internalMultiValue, setInternalMultiValue] = useState<T[]>(
    (Array.isArray(value) ? (value as T[]) : []) ?? []
  );

  // References
  // debounce timer for calling handleValueChange callback
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // track pending selected value updates
  const pendingMultiRef = useRef<{ keys: string[]; values: T[] } | null>(null);

  // tracks pending selected value keys
  const pendingKeysRef = React.useRef<string[] | null>(null);

  //Stabilize the supplied getOptionLabel and getOptionKey callbacks
  const stableGetOptionLabel = useCallback<GetOptionLabel<T>>(
    (o: T) => getOptionLabel(o),
    [getOptionLabel]
  );
  const stableGetOptionKey = useCallback<GetOptionKey<T>>(
    (o: T) => getOptionKey(o),
    [getOptionKey]
  );

  // helper to extract string keys (order-insensitive compare)
  const getKeysForOptions = useCallback(
    (arr: T[] | undefined) => (arr ?? []).map((o) => stableGetOptionKey(o)),
    [stableGetOptionKey]
  );

  // Memoize the fuzzy search filter for the set of options.
  // Only rebuild index when options or label logic changes.
  const fuzzyFilter = useMemo(
    () => CreateFuzzySearchFilter(options, stableGetOptionLabel),
    [options, stableGetOptionLabel]
  );

  //Handle the search text
  useEffect(() => {
    // don’t overwrite while user is typing in search
    if (searchable && dropdownOpen) return;

    // always blank when not actively searching
    setInputDisplay("");
  }, [dropdownOpen, searchable]);

  // Much of the component behavior depends on
  // whether we are allowing multiple selected values
  const isMultiselect = multiselect === true;

  // Create type safe wrappers for handleValueChange and supply a default noop function
  const noopSingle: (k: string | null, v: T | null) => void = () => {
    return;
  };
  const noopMulti: (ks: string[], vs: T[]) => void = () => {
    return;
  };
  const onChangeSingleSelect =
    (handleValueChange as
      | ((key: string | null, value: T | null) => void)
      | undefined) ?? noopSingle;
  const onChangeMultiSelect =
    (handleValueChange as
      | ((keys: string[], values: T[]) => void)
      | undefined) ?? noopMulti;

  // ---------------- DEBOUNCE LOGIC (multi-select) ----------------

  //Commit pending multiselect changes
  const flushPendingMultiChange = useCallback(() => {
    //Turn off timer
    if (debounceTimerRef.current !== null) {
      globalThis.clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }

    //Process the pending changes
    const p = pendingMultiRef.current;
    if (p) {
      onChangeMultiSelect(p.keys, p.values);

      //clear pending
      pendingMultiRef.current = null;
      pendingKeysRef.current = null;
    }
  }, [onChangeMultiSelect]);

  //Schedule a change to multiple select values
  const scheduleMultiChange = useCallback(
    (keys: string[], values: T[]) => {
      // Store the latest pending selected values and keys
      pendingMultiRef.current = { keys, values };
      pendingKeysRef.current = keys.map((k) => String(k));

      // if debounce disabled or zero -> commit change immediately
      if (!debounceMs || debounceMs <= 0) {
        flushPendingMultiChange();
        return;
      }

      // Clear any existing timer
      if (debounceTimerRef.current !== null) {
        globalThis.clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }

      // Set a timer to commit the pending changes
      debounceTimerRef.current = globalThis.setTimeout(() => {
        //Get a reference to the pending update
        const p = pendingMultiRef.current;

        //Clear the pending update
        pendingMultiRef.current = null;
        pendingKeysRef.current = null;
        debounceTimerRef.current = null;

        if (p) {
          onChangeMultiSelect(p.keys, p.values);
        }
      }, debounceMs);
    },
    [debounceMs, onChangeMultiSelect, flushPendingMultiChange]
  );

  // When the parent-controlled `value` changes from outside, sync internal state.
  useEffect(() => {
    if (!isMultiselect) return;

    const incomingValues = (value as T[]) ?? [];
    const incomingKeys = getKeysForOptions(incomingValues);
    const internalKeys = getKeysForOptions(internalMultiValue);

    // If exactly equal, nothing to do
    if (isSameKeySet(incomingKeys, internalKeys)) {
      return;
    }

    // If we have a pending change:
    if (pendingKeysRef.current) {
      // If parent now reflects the pending change, accept it and clear pending.
      if (isSameKeySet(incomingKeys, pendingKeysRef.current)) {
        setInternalMultiValue(incomingValues);

        //Clear pending change
        pendingMultiRef.current = null;
        pendingKeysRef.current = null;

        // Clear any existing timer
        if (debounceTimerRef.current !== null) {
          globalThis.clearTimeout(debounceTimerRef.current);
          debounceTimerRef.current = null;
        }

        return;
      }

      // Parent sent something else while we have a pending change.
      // Keep optimistic internal state — do NOT overwrite.
      return;
    }

    // No pending change: parent is authoritative — accept incoming state.
    setInternalMultiValue(incomingValues);

    // NOTE: internalMultiValue is intentionally NOT in deps: we only sync *from parent value* changes,
    // not on every optimistic internal update, to avoid clobbering optimistic changes.
  }, [value]);

  // ---------------- end debounce logic ----------------

  //Handles the logic for determining the placeholder text
  const getPlaceholder = (): string | undefined => {
    //If the dropdown is open, and we are searching, indicate we are in search
    if (dropdownOpen && searchable) return "search...";

    //If multiselect, show the placeholder the caller provided
    if (isMultiselect) return placeholder;

    //If we have no value, show the placeholder the caller provided
    if (Array.isArray(value) && value.length === 0) return placeholder;
    if (!Array.isArray(value) && (value === null || value === undefined))
      return placeholder;

    return undefined;
  };

  // Handle dropdown open
  const handleDropDownOpen = () => {
    if (disabled) return;
    // Clear the input text when we open the dropdown for search
    if (searchable) {
      setInputDisplay("");
    }
    setDropdownOpen(true);
  };

  // Handle dropdown close
  const handleDropDownClose = () => {
    setDropdownOpen(false);

    // when user closes the dropdown, push any pending changes immediately
    flushPendingMultiChange();
  };

  // Handle selected options change
  const handleOnChange = (
    _event: React.SyntheticEvent,
    selectedOptions: T | T[] | null
  ) => {
    //Determine from multiselect the type safe change event to call
    if (isMultiselect) {
      const values = (selectedOptions as T[]) ?? [];
      const keys = values.map((o: T) => stableGetOptionKey(o));
      setInternalMultiValue(values); //update UI selected values immediately (optimistic)
      scheduleMultiChange(keys, values); //schedule debounced commit of change
    } else {
      const value = (selectedOptions as T) ?? null;
      const key = value == null ? null : stableGetOptionKey(value);
      onChangeSingleSelect(key, value);
    }
  };

  // Handle changes to the input field
  const handleInputChange = (
    _event: React.SyntheticEvent,
    newValue: string,
    reason: AutocompleteInputChangeReason
  ) => {
    // After selecting an option, MUI sends reason === "reset".
    // - multi select: keep current text (stay filtered)
    // - single select: clear text so only the selected value is shown
    if (reason === "reset") {
      if (isMultiselect) return;
      setInputDisplay("");
      return;
    }

    // If user typed into the text box, but we aren't in search mode, ignore
    if (reason === "input" && !searchable) return;

    // Update our input display prop with our typed value
    setInputDisplay(newValue ?? "");
  };

  // Options and values are compared with their keys.
  const isOptionEqualToValue = (option: T, value: T) =>
    value != null && stableGetOptionKey(option) === stableGetOptionKey(value);

  return (
    <Wrapper
      backgroundColor={backgroundColor}
      className={className}
      height={height}
      id={id}
      noShadow={noShadow}
      noHoverBorder={noHoverBorder}
      $multiselect={isMultiselect}
      style={style}
      tabIndex={tabIndex}
      onClick={() => {
        // Input may be collapsed when a value is selected; open on click.
        if (!disabled && !dropdownOpen) {
          setDropdownOpen(true);
        }
      }}
      onFocus={() => {
        if (!disabled && !dropdownOpen) {
          setDropdownOpen(true);
        }
      }}
    >
      <Autocomplete
        autoHighlight={true}
        blurOnSelect={!isMultiselect}
        componentsProps={{
          popupIndicator: {
            title: "", // Tooltip text is removed
          },
        }}
        disableClearable={!clearable}
        disableCloseOnSelect={isMultiselect}
        disabled={disabled}
        disablePortal
        inputValue={inputDisplay}
        multiple={isMultiselect}
        open={!disabled && dropdownOpen}
        options={options}
        popupIcon={<KeyboardArrowDownIcon />}
        selectOnFocus={false}
        size="small"
        value={isMultiselect ? (internalMultiValue as any) : (value as any)}
        filterOptions={(_options, state) => fuzzyFilter(state)}
        getOptionLabel={stableGetOptionLabel}
        getOptionKey={stableGetOptionKey}
        isOptionEqualToValue={isOptionEqualToValue}
        onOpen={handleDropDownOpen}
        onChange={handleOnChange}
        onClose={handleDropDownClose}
        onInputChange={handleInputChange}
        renderTags={(tagValue, getTagProps) =>
          tagValue.map((option, index) => {
            const { key, ...tagProps } = getTagProps({ index });
            return (
              <Chip
                key={key}
                size="small"
                label={stableGetOptionLabel(option)}
                {...(getChipProps?.(option) ?? {})}
                {...tagProps}
              />
            );
          })
        }
        renderInput={(params) => {
          // Collapse the search input when closed with a selection so the chip
          // row doesn't reserve a second line for an empty search field.
          const hasSelection = isMultiselect
            ? Array.isArray(value) && value.length > 0
            : value !== null && value !== undefined;
          const hideInput = Boolean(hasSelection && !dropdownOpen);

          const inputClassName = [
            params.inputProps?.className,
            hideInput ? "SearchableDropdown-input--hidden" : null,
          ]
            .filter(Boolean)
            .join(" ");

          return (
            <TextField
              {...params}
              name={name}
              placeholder={getPlaceholder()}
              InputProps={{
                ...params.InputProps,
                // Add a chip for single-select when a value exists
                startAdornment: (
                  <>
                    {params.InputProps.startAdornment}
                    {!isMultiselect &&
                    !Array.isArray(value) &&
                    value !== null &&
                    value !== undefined ? (
                      <Chip
                        size="small"
                        label={stableGetOptionLabel(value)}
                        {...(getChipProps?.(value) ?? {})}
                        // allow clearing via chip “x” if clearable is true
                        onDelete={
                          clearable && !disabled
                            ? () => onChangeSingleSelect(null, null)
                            : undefined
                        }
                      />
                    ) : null}
                  </>
                ),
              }}
              // props passed to underlying input control in TextField
              inputProps={{
                ...params.inputProps,
                className: inputClassName,
                readOnly: !searchable,
                // keep other attributes as-is (e.g. ref, aria)
              }}
              onBlur={onBlur}
            />
          );
        }}
      />
    </Wrapper>
  );
}

const Wrapper = styled.div<any>`
  min-width: 150px;

  /**
 * Main wrapper
 */
  .MuiAutocomplete-root {
    width: 100%;
    max-width: 400px;
    position: relative;
    box-sizing: border-box;
    font-family: "Roboto", Helvetica, Arial, sans-serif;
    cursor: pointer;
  }

  .MuiAutocomplete-root *,
  .MuiAutocomplete-root *::after,
  .MuiAutocomplete-root *::before {
    box-sizing: inherit;
    font-family: "Roboto", Helvetica, Arial, sans-serif;
  }

  .MuiAutocomplete-root * {
    cursor: pointer;
  }

  .MuiAutocomplete-inputRoot .MuiOutlinedInput-notchedOutline {
    border: none;
  }

  /**
   * Popper (dropdown) and children styling
  */

  //Normal dropdown option, unselected, unfocused
  .MuiAutocomplete-option {
    font-size: ${(props) => props.fontSize || "14px"};
  }

  //Drop down option, selected, unfocused
  .MuiAutocomplete-option[aria-selected="true"] {
    background-color: #bce4ff !important;
  }

  //Drop down option, selected, focused
  .MuiAutocomplete-option.Mui-focused[aria-selected="true"] {
    background-color: #7dcbff !important;
  }

  //Separator line between the dropdown entries
  .MuiAutocomplete-option:not(:first-of-type) {
    border-top: 1px solid #eee;
  }

  /**
 * Input
 */
  .MuiAutocomplete-input::-webkit-search-decoration,
  .MuiAutocomplete-input::-webkit-search-cancel-button,
  .MuiAutocomplete-input::-webkit-search-results-button,
  .MuiAutocomplete-input::-webkit-search-results-decoration {
    -webkit-appearance: none;
  }

  // Ensures parent of input component does not have left padding so that
  // input component starts all the way to the left by default.
  &&& .MuiAutocomplete-inputRoot {
    padding-left: 0;
    // Chips may wrap; search stays on the current line until min-width won't fit.
    flex-wrap: wrap;
    align-items: center;
    /* Replace MUI tag margins — keeps single-line compact and spaces wrapped rows. */
    gap: 4px;
  }

  &&& .MuiAutocomplete-tag {
    margin: 0;
    align-self: center;
  }

  // Root needs to be repeated 4 times so that specificity is high
  // enough to override MUI's built-in styling.
  &&&& .MuiAutocomplete-input {
    box-sizing: border-box;
    font-family: "Roboto", Helvetica, Arial, sans-serif;
    display: block;
    height: ${(props) => (props.height ? props.height : "16px")};
    /*
     * Don't use width:100% — that forces the search onto its own row.
     * width:0 + flex-grow keeps it beside chip(s); it only wraps when
     * there isn't room for min-width on the current line.
     */
    width: 0;
    min-width: 64px;
    flex: 1 1 64px;
    padding: ${(props) => (props.noHoverBorder ? 0 : "0px 16px")};
    /* Space between chip(s) and the search field */
    padding-left: 8px;
    background: ${(props) => props.backgroundColor};
    border: 1px solid transparent;
    box-shadow: ${(props) =>
      props.noShadow ? "none" : "0 0.0625rem 0.125rem rgba(0, 0, 0, 0.15)"};
    border-radius: 3px;
    outline: none;
    font-size: ${(props) => props.fontSize || "14px"};
    text-align: left;
    text-overflow: ellipsis;
    line-height: 36px;
    -webkit-appearance: none;
    box-sizing: border-box;
    min-height: 14px;
    &::placeholder {
      font-size: ${(props) => props.fontSize || "14px"};
    }
  }

  /*
   * Closed with a selection: fully collapse the search input.
   * Otherwise flex-basis / line-height leave an empty second row under chips.
   */
  &&&& .MuiAutocomplete-input.SearchableDropdown-input--hidden {
    width: 0 !important;
    min-width: 0 !important;
    flex: 0 0 0 !important;
    height: 0 !important;
    min-height: 0 !important;
    max-height: 0 !important;
    line-height: 0 !important;
    padding: 0 !important;
    margin: 0 !important;
    border: 0 !important;
    opacity: 0 !important;
    overflow: hidden !important;
    pointer-events: none !important;
    caret-color: transparent !important;
  }

  /**
 * States
 */
  .MuiAutocomplete-root:has(.Mui-disabled) * {
    cursor: default;
  }

  &&& .MuiAutocomplete-popupIndicator:hover {
    background-color: transparent;
  }

  .MuiAutocomplete-inputRoot:hover .MuiOutlinedInput-notchedOutline {
    border: ${(props) =>
      props.noHoverBorder
        ? "none"
        : "solid 1px " + props.theme.colors.secondary};
  }
`;
